import './optionSelectorContainer.scss';

import { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, ButtonColor, ButtonVariant, Size } from '@eliberty/design-system-react';
import classNames from 'classnames';
import * as DOMPurify from 'dompurify';

import { FormattedCurrency, Spoiler } from '~atoms';
import { AvailableOption } from '~types/appsync.types';

interface OptionSelectorContainerProps {
  option: AvailableOption;
  currency: string;
  isOptionSelected: boolean;
  isMandatory?: boolean;
  selectedCount?: number;
  summary?: string;
  onAddAll: () => void;
  onRemoveAll: () => void;
  onEdit?: () => void;
  children?: ReactNode;
}

const OptionSelectorContainer = ({
  option,
  currency,
  isOptionSelected,
  isMandatory = false,
  selectedCount,
  summary,
  onAddAll,
  onRemoveAll,
  onEdit,
  children,
}: OptionSelectorContainerProps) => {
  const intl = useIntl();

  const sanitizedDescription = DOMPurify.default.sanitize(option.description ?? '');
  const isFreeOption = !option.priceFrom;
  const showCollapsedView = isOptionSelected && summary !== undefined;
  const hasDescription = Boolean(option.description);
  const showActions = isOptionSelected && !isMandatory;
  const isCollapsedClickable = showCollapsedView && !!onEdit && !isMandatory;

  const collapsedContent = (
    <>
      <div className='OptionSelectorContainer_header'>
        <span className='OptionSelectorContainer_name'>{option.name}</span>
        {selectedCount != null && (
          <span className='OptionSelectorContainer_count'>x{selectedCount}</span>
        )}
      </div>
      {summary && <div className='OptionSelectorContainer_summary'>{summary}</div>}
      {!isMandatory && (
        <div className='OptionSelectorContainer_actions'>
          <Button
            color={ButtonColor.Accent}
            variant={ButtonVariant.Ghost}
            size={Size.S}
            onClick={(e) => {
              e.stopPropagation();
              onRemoveAll();
            }}
          >
            <FormattedMessage defaultMessage='Remove' id='remove' />
          </Button>
          <Button
            variant={ButtonVariant.Ghost}
            size={Size.S}
            onClick={(e) => {
              e.stopPropagation();
              onEdit?.();
            }}
          >
            <FormattedMessage defaultMessage='Edit' id='edit' />
          </Button>
        </div>
      )}
    </>
  );

  const CollapsedWrapper = isCollapsedClickable ? 'button' : 'div';

  return (
    <div className='OptionSelectorContainer'>
      <div
        className={classNames('OptionSelectorContainer_card', {
          'OptionSelectorContainer_card-active': isOptionSelected,
        })}
      >
        {showCollapsedView ? (
          <CollapsedWrapper
            {...(isCollapsedClickable && { onClick: onEdit, type: 'button' })}
            className={classNames('OptionSelectorContainer_collapsed', {
              'OptionSelectorContainer_collapsed-clickable': isCollapsedClickable,
            })}
          >
            {collapsedContent}
          </CollapsedWrapper>
        ) : (
          <>
            <div className='OptionSelectorContainer_content'>
              <div className='OptionSelectorContainer_top'>
                <div className='OptionSelectorContainer_left'>
                  <div className='OptionSelectorContainer_name'>{option.name}</div>
                  <div className='OptionSelectorContainer_price'>
                    <span className='OptionSelectorContainer_priceValue'>
                      {isFreeOption ? (
                        <FormattedMessage defaultMessage='Free' id='free' />
                      ) : (
                        <FormattedCurrency value={option.priceFrom ?? 0} currency={currency} />
                      )}
                    </span>
                    {!isFreeOption && (
                      <span className='OptionSelectorContainer_priceLegend'>
                        <FormattedMessage defaultMessage='Per adult' id='perAdult' />
                      </span>
                    )}
                  </div>
                </div>
                {!isMandatory && !isOptionSelected && (
                  <Button
                    leadingIcon='action-plus'
                    size={Size.S}
                    onClick={onAddAll}
                    aria-label={intl.formatMessage({
                      defaultMessage: 'Add option to all participants',
                      id: 'addOptionToAllParticipants',
                    })}
                  >
                    <FormattedMessage defaultMessage='Add' id='add' />
                  </Button>
                )}
              </div>
              {hasDescription && !isOptionSelected && (
                <Spoiler maxHeight={58}>
                  <div className='OptionSelectorContainer_description'>
                    <div
                      className='OptionSelectorContainer_descriptionText'
                      dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
                    />
                  </div>
                </Spoiler>
              )}
            </div>

            {isOptionSelected && children}

            {showActions && (
              <div className='OptionSelectorContainer_actions'>
                <Button
                  color={ButtonColor.Accent}
                  variant={ButtonVariant.Ghost}
                  size={Size.S}
                  onClick={onRemoveAll}
                >
                  <FormattedMessage defaultMessage='Remove' id='remove' />
                </Button>
                {onEdit && (
                  <Button variant={ButtonVariant.Ghost} size={Size.S} onClick={onEdit}>
                    <FormattedMessage defaultMessage='Edit' id='edit' />
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default OptionSelectorContainer;
